import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnInit
} from '@angular/core';
import { ActivatedRoute, RouteReuseStrategy, Router } from '@angular/router';
import { merge } from 'lodash';
import { CompanyVendor, User } from 'src/app/classes/models';
import { ReloadRouteReuseStrategy } from 'src/app/classes/routing/reload-reuse-strategy';
import { GridActionColumnActions } from 'src/app/interfaces/grid-action-column-actions';
import { GridActionColumnMap } from 'src/app/interfaces/grid-action-column-map';
import { AuthService } from 'src/app/services/auth.service';
import { ToastService } from 'src/app/services/toast.service';
import { UtilityService } from 'src/app/services/utility.service';

@Component({
  selector: 'app-compliance',
  templateUrl: './compliance.component.html',
  styleUrls: ['./compliance.component.less'],
  providers: [
    { provide: RouteReuseStrategy, useClass: ReloadRouteReuseStrategy }
  ],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class ComplianceComponent implements OnInit {
  constructor(
    private route: ActivatedRoute,
    private changeDetection: ChangeDetectorRef,
    private router: Router,
    private ToastService: ToastService,
    private AuthService: AuthService,
    private UtilityService: UtilityService
  ) {
    const navigation = this.router.currentNavigation();
    const extras = navigation?.extras;

    this.LoggedInUser = this.AuthService.getUser();
    const { UserAccountRole: { role } = {} } = this.LoggedInUser;

    if (!role) {
      throw this.ToastService.addErrorToast(
        'Error',
        'Missing user permission.',
        { returnError: true }
      );
    }

    this.ColumnIdToColumnActions = {
      ['$actionColumn']: ['edit']
    };

    if (
      new Set([
        'ADMIN_ADMIN',
        'ADMIN_FRAUD_TEAM',
        'ADMIN_CUSTOMER_SERVICE_TEAM',
        'ADMIN_PROJECT_MANAGEMENT_TEAM'
      ]).has(role)
    ) {
      this.ColumnIdToColumnActions['$actionColumn'].push('play');
    }

    if (extras) {
      const { state } = extras;

      if (state) {
        const { displayCount, query } = state;

        if (displayCount) {
          this.isDashboardCount = true;
          const { query } = displayCount.value;
          merge(
            this.suppliedQueryData,
            this.UtilityService.parseStringsInQueryObject(query)
          );

          this.title = displayCount.key;
        }

        if (query) {
          this.isDashboardCount = true;
          merge(
            this.suppliedQueryData,
            this.UtilityService.parseStringsInQueryObject(query)
          );

          this.title = 'Vendors';
        }
      }
    }
  }

  title?: string;
  viveStatus: string | null = null;
  Filters: Map<string, any> = new Map();
  initialized: boolean = false;
  isDashboardCount: boolean = false;
  showArchived: boolean = false;
  reloadGrid: boolean = false;
  LoggedInUser: User;

  ColumnIdToColumnActions: GridActionColumnMap;
  suppliedQueryData: { [key: string]: any } = {
    isArchived: false
  };

  calculatedColumnActions({
    rowData
  }: {
    rowData: CompanyVendor;
  }): GridActionColumnActions[] | null {
    const gridActionColumnActionArr: GridActionColumnActions[] = ['edit'];
    const { VendorId } = rowData;

    if (VendorId) {
      gridActionColumnActionArr.push('play');
    }

    return gridActionColumnActionArr;
  }

  actionEvent({
    action,
    rowData
  }: {
    action: GridActionColumnActions;
    rowData: CompanyVendor;
  }) {
    const {
      id,
      CompanyId,
      VendorId,
      ClientRegisteredVendorId,
      ExemptVendorId,
      CompanyComplianceTypeId
    } = rowData;
    if (!id) {
      throw this.ToastService.addErrorToast(
        'Error',
        'Missing Client/Vendor Id.',
        { returnError: true }
      );
    }

    if (!CompanyId) {
      throw this.ToastService.addErrorToast(
        'Error',
        'Missing Client/Vendor Id.',
        { returnError: true }
      );
    }

    if (!VendorId && !ClientRegisteredVendorId && !ExemptVendorId) {
      throw this.ToastService.addErrorToast(
        'Error',
        'Cannot complete action. Missing Vendor ID.',
        { returnError: true }
      );
    }

    const navArray: any[] = [
      'admin',
      'run-vive',
      'client-vendor',
      id,
      'client',
      CompanyId
    ];

    if (VendorId) {
      if (action === 'edit' || action === 'play') {
        navArray.push(
          ...[
            'vendor',
            VendorId,
            'compliance-type',
            CompanyComplianceTypeId,
            'action'
          ]
        );
        if (action === 'edit') {
          navArray.push('edit');
        }
        if (action === 'play') {
          navArray.push('review');
        }
        navArray.push('business-review');
      }
    }

    if (ClientRegisteredVendorId) {
      if (action === 'edit') {
        navArray.push(
          ...[
            'client-registered-vendor',
            ClientRegisteredVendorId,
            'action',
            'edit',
            'overview'
          ]
        );
      }
    }

    if (ExemptVendorId) {
      if (action === 'edit') {
        navArray.push(
          ...['exempt-vendor', ExemptVendorId, 'action', 'edit', 'overview']
        );
      }
    }

    const url = this.router.serializeUrl(this.router.createUrlTree(navArray));

    window.open(url, '_blank');
  }

  onShowArchivedChange() {
    this.showArchived = !this.showArchived;

    this.updateGridWithShowArchived();
  }

  updateGridWithShowArchived() {
    if (this.showArchived) {
      Object.assign(this.suppliedQueryData, {
        isArchived: { $or: [true, false] }
      });
    } else {
      Object.assign(this.suppliedQueryData, { isArchived: false });
    }

    this.reloadGrid = true;
  }

  // This method can be used to re-initialize this component when the route parameter is changed.
  // This way, the grid state is reset to a base state.
  reinitialize() {
    this.initialized = false;
    this.changeDetection.detectChanges();
    this.initialized = true;

    this.updateGridWithShowArchived();
  }

  setTitleToViveStatus() {
    const viveStatus = this.viveStatus || 'All';
    this.title = `VIVE Vetting - ${viveStatus} vendors`;
  }

  ngOnInit(): void {
    this.initialized = true;
    this.route.params.subscribe({
      next: (routeParam) => {
        const { viveStatus } = routeParam;
        this.viveStatus = viveStatus;

        if (this.viveStatus === '') {
          this.setTitleToViveStatus();
          this.suppliedQueryData = {};
          this.isDashboardCount = false;
        }

        if (this.viveStatus) {
          this.suppliedQueryData = {};
          this.isDashboardCount = false;
          this.setTitleToViveStatus();
          this.Filters.set('viveStatus', this.viveStatus);
        } else {
          this.Filters.delete('viveStatus');
        }

        this.reinitialize();
      }
    });
  }
}
